import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useAuth } from '../hooks/useAuth'
import { productsAPI } from '../services/productsAPI'
import Card, { CardHeader, CardTitle, CardContent } from '../components/UI/Card'
import Button from '../components/UI/Button'
import Input from '../components/UI/Input'
import AddPermissionModal from '../components/Modals/AddPermissionModal'
import ConfirmationModal from '../components/Modals/ConfirmationModal'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import { Plus, Search, Edit, Trash2, ToggleLeft, ToggleRight } from 'lucide-react'
import toast from 'react-hot-toast'

export default function HubFeatures() {
  const { user, hasPermission, isControlCenter } = useAuth()
  const queryClient = useQueryClient()
  const [permissionSearch, setPermissionSearch] = useState('')
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false)
  const [editingPermission, setEditingPermission] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [permissionToDelete, setPermissionToDelete] = useState(null)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  
  // Fetch permissions from API
  const { data: permissionsData, isLoading, refetch } = useQuery(
    ['hubPermissions'],
    () => productsAPI.getHubPermissions(),
    {
      select: (response) => response.data?.data?.permissions || [],
      onError: (error) => {
        console.error('Error fetching hub permissions:', error)
        toast.error('Failed to load permissions')
      }
    }
  )

  const permissions = permissionsData || []

  const openAddPermissionModal = () => {
    setEditingPermission(null)
    setIsPermissionModalOpen(true)
  }

  const openEditPermissionModal = (permission) => {
    setEditingPermission(permission)
    setIsPermissionModalOpen(true)
  }

  const closePermissionModal = () => {
    setIsPermissionModalOpen(false)
    setEditingPermission(null)
  }

  // Create permission mutation
  const createPermissionMutation = useMutation(
    (permissionData) => productsAPI.createHubPermission(permissionData),
    {
      onSuccess: () => {
        toast.success('Permission created successfully!')
        queryClient.invalidateQueries(['hubPermissions'])
        closePermissionModal()
      },
      onError: (error) => {
        console.error('Error creating permission:', error)
        toast.error(error.response?.data?.message || 'Failed to create permission')
      }
    }
  )

  // Update permission mutation
  const updatePermissionMutation = useMutation(
    ({ featureId, permissionData }) => productsAPI.updateFeature(featureId, permissionData),
    {
      onSuccess: () => {
        toast.success('Permission updated successfully!')
        queryClient.invalidateQueries(['hubPermissions'])
        closePermissionModal()
      },
      onError: (error) => {
        console.error('Error updating permission:', error)
        toast.error(error.response?.data?.message || 'Failed to update permission')
      }
    }
  )

  // Delete permission mutation
  const deletePermissionMutation = useMutation(
    (featureId) => productsAPI.deleteFeature(featureId),
    {
      onSuccess: () => {
        toast.success('Permission deleted successfully!')
        queryClient.invalidateQueries(['hubPermissions'])
        setShowDeleteModal(false)
        setPermissionToDelete(null)
      },
      onError: (error) => {
        console.error('Error deleting permission:', error)
        const errorMsg = error.response?.data?.message || 'Failed to delete permission'
        // Show error in modal instead of toast for better visibility
        setErrorMessage(errorMsg)
        setShowErrorModal(true)
        setShowDeleteModal(false)
      }
    }
  )

  // Toggle permission active status mutation
  const togglePermissionMutation = useMutation(
    ({ featureId, isActive }) => productsAPI.updateFeature(featureId, { is_active: isActive }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['hubPermissions'])
      },
      onError: (error) => {
        console.error('Error toggling permission:', error)
        toast.error('Failed to update permission status')
      }
    }
  )

  const handlePermissionSubmit = async (data) => {
    setIsSubmitting(true)
    try {
      if (editingPermission) {
        // Update existing permission
        await updatePermissionMutation.mutateAsync({
          featureId: editingPermission.id,
          permissionData: {
            name: data.name,
            description: data.description,
            resource: data.resource,
            action: data.action, // Will be stored in permissions JSONB
            is_active: data.active
          }
        })
      } else {
        // Create new permission
        await createPermissionMutation.mutateAsync({
          name: data.name,
          description: data.description,
          resource: data.resource,
          action: data.action,
          is_active: data.active
        })
      }
    } catch (error) {
      // Error handled in mutation onError
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleTogglePermission = (permission) => {
    togglePermissionMutation.mutate({
      featureId: permission.id,
      isActive: !permission.is_active
    })
  }

  const handleDeleteClick = (permission) => {
    setPermissionToDelete(permission)
    setShowDeleteModal(true)
  }

  const handleDeleteConfirm = () => {
    if (permissionToDelete) {
      deletePermissionMutation.mutate(permissionToDelete.id)
    }
  }

  const handleDeleteCancel = () => {
    setShowDeleteModal(false)
    setPermissionToDelete(null)
  }

  // Filter permissions based on search
  const filteredPermissions = permissions.filter(permission => {
    const searchLower = permissionSearch.toLowerCase()
    const permissionAction = permission.permissions?.action || ''
    return (
      (permission.name || '').toLowerCase().includes(searchLower) ||
      (permission.resource || '').toLowerCase().includes(searchLower) ||
      permissionAction.toLowerCase().includes(searchLower)
    )
  })


  if (!isControlCenter()) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-secondary-900 mb-4">Access Denied</h2>
        <p className="text-secondary-600">You don't have permission to view this page.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Hub Features</h1>
          {/* <p className="text-secondary-600 mt-1">Manage hub features and capabilities</p> */}
        </div>
      </div>

      {/* Section Header and Search */}
      <div className="flex items-center justify-between mt-4">
        <Button onClick={openAddPermissionModal}>
          <Plus className="h-4 w-4 mr-2" />
          Add Permission
        </Button>
      </div>
      <Card className="mt-4">
        <CardContent className="p-4">
          <label className="block text-xs font-medium text-secondary-600 mb-2">Search</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
            <Input
              placeholder="Search permissions..."
              value={permissionSearch}
              onChange={(e) => setPermissionSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Permissions Table */}
      <Card className="mt-4">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Permission Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Resource
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Permission
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-200">
                {isLoading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <LoadingSpinner />
                    </td>
                  </tr>
                ) : filteredPermissions.length > 0 ? (
                  filteredPermissions.map((permission) => (
                    <tr key={permission.id} className="hover:bg-secondary-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900">
                        {permission.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-secondary-700">
                        {permission.description || '-'}
                      </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-700">
                        {permission.resource}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-700">
                        {(() => {
                          // Extract permission key from permissions object (e.g., "organizations.create")
                          if (permission.permissions && typeof permission.permissions === 'object') {
                            // Handle new format: {"organizations.create": true}
                            const permissionKeys = Object.keys(permission.permissions).filter(key => 
                              permission.permissions[key] === true && key !== 'action'
                            );
                            if (permissionKeys.length > 0) {
                              return permissionKeys[0];
                            }
                            // Handle old format: {"action": "create"} - construct from resource and action
                            if (permission.permissions.action && permission.resource) {
                              return `${permission.resource}.${permission.permissions.action}`;
                            }
                          }
                          return '-';
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleTogglePermission(permission)}
                          className="inline-flex items-center focus:outline-none"
                          disabled={togglePermissionMutation.isLoading}
                          title={permission.is_active ? 'Disable' : 'Enable'}
                        >
                          {permission.is_active ? (
                            <ToggleRight className="h-6 w-6 text-green-600" />
                          ) : (
                            <ToggleLeft className="h-6 w-6 text-gray-400" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => openEditPermissionModal(permission)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                            title="Edit Permission"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(permission)}
                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                            title="Delete Permission"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                          </td>
                        </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-sm text-secondary-500">
                      {permissionSearch ? 'No permissions found matching your search.' : 'No permissions available. Click "Add Permission" to create one.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Permission Modal */}
      <AddPermissionModal
        isOpen={isPermissionModalOpen}
        onClose={closePermissionModal}
        onSubmit={handlePermissionSubmit}
        permission={editingPermission}
        isSubmitting={isSubmitting}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Permission"
        message={`Are you sure you want to delete "${permissionToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Error Modal */}
      <ConfirmationModal
        isOpen={showErrorModal}
        onClose={() => {
          setShowErrorModal(false)
          setErrorMessage('')
        }}
        onConfirm={() => {
          setShowErrorModal(false)
          setErrorMessage('')
        }}
        title="Cannot Delete Permission"
        message={errorMessage || 'An error occurred while trying to delete the permission.'}
        confirmText="Got it"
        cancelText={null}
        type="warning"
      />
    </div>
  )
}
